import {
  createAPIHooks,
  APIClient,
  type APIQueryHooks,
} from '@lifeomic/one-query';
import type { RestAPIEndpoints } from '@lifeomic/react-client/rest-api-types';
import type { Endpoints as MemberOperationsServiceMembersEndpoints } from '@lifeomic/service-clients/member-operations-service-members';
import type { RegenerativeMedicineRestEndpoints } from '../types/regenerativeMedicineRestEndpoints';
import useServiceAuth from './useServiceAuth';

/** Routes not yet in the installed `@lifeomic/react-client` `RestAPIEndpoints` (remove when dep ships them). */
type PhcRestEndpointExtensions = RegenerativeMedicineRestEndpoints &
  MemberOperationsServiceMembersEndpoints & {
    'POST /v1/agents-v2/document-extraction-v2/resources/:resource_type/:resource_id/feedback': {
      Request: {
        resource_type:
          | 'AllergyIntolerance'
          | 'Condition'
          | 'Immunization'
          | 'MedicationStatement'
          | 'Observation'
          | 'Procedure';
        resource_id: string;
        subject_id: string;
        project_id: string;
        decision: 'approved' | 'rejected';
        task_id?: string;
        comment?: string;
      };
      Response: {
        resource_type: string;
        resource_id: string;
        decision: 'approved' | 'rejected';
        deleted: boolean;
        approved_by?: string | null;
        approved_at?: string | null;
      };
    };
    'POST /v1/agents-v2/document-extraction-v2/observations/:observation_id/feedback': {
      Request: {
        observation_id: string;
        subject_id: string;
        project_id: string;
        decision: 'approved' | 'rejected';
        task_id?: string;
        comment?: string;
      };
      Response: {
        observation_id: string;
        decision: 'approved' | 'rejected';
        deleted: boolean;
        approved_by?: string | null;
        approved_at?: string | null;
      };
    };
  };

export type PhcPlatformRestEndpoints = RestAPIEndpoints &
  PhcRestEndpointExtensions;
import axios from 'axios';
import { useMemo } from 'react';
import {
  withE2ERequestStubbing,
  withAccountHeader,
  withAuthHeader,
  withConfiguredBaseUrl,
  withRetries,
  compose,
} from './useService';
import withPathPrefix from './useService/util/withPathPrefix';
import { QueryClient } from '@tanstack/react-query';
import { isInternalQueryKey } from '@lifeomic/one-query/util';

const useOneQueryClient = () => {
  const auth = useServiceAuth();

  const client = useMemo(() => {
    const axiosInstance = axios.create();
    return compose(
      withE2ERequestStubbing,
      withAccountHeader(auth.account),
      withAuthHeader(auth.authToken),
      withConfiguredBaseUrl(auth.config),
      withPathPrefix('/api'),
      withRetries(),
    )(axiosInstance);
  }, [auth.authToken, auth.account, auth.config]);

  return client;
};

/**
 * Axios client for PHC `/api` REST calls (auth, base URL, retries).
 * Use when a route needs non-default request shaping (e.g. POST with query params).
 */
export const usePhcRestAxios = useOneQueryClient;

export const REST_API_HOOKS_NAME = 'phc-ui';

const phcRestApiHooks = createAPIHooks<PhcPlatformRestEndpoints>({
  name: REST_API_HOOKS_NAME,
  client: () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useOneQueryClient();
  },
});

/**
 * `react-query`-style hooks for the platform REST API.
 */
// Prefer `createAPIHooks<RestAPIEndpoints>`: react-client's emitted `createRestAPIHooks` return type omits newer routes.
export const apiHooks: APIQueryHooks<PhcPlatformRestEndpoints> =
  phcRestApiHooks;

/**
 * Returns an `axios`-style client for the platform REST API.
 * Memoized so consumers can safely list this in `useEffect` dependency arrays
 * without re-running effects every render (the underlying axios instance already
 * updates when auth/config changes).
 */
export const useApiClient = (): APIClient<PhcPlatformRestEndpoints> => {
  const client = useOneQueryClient();
  return useMemo(
    () => new APIClient<PhcPlatformRestEndpoints>(client),
    [client],
  );
};

/**
 * Updates the cached data for queries matching a specific route and predicate in the given query client.
 *
 * @param queryClient - The QueryClient instance whose queries' data will be updated.
 * @param route - The API route string to match against each query's internal query key.
 * @param predicate - A function that receives the query payload and returns true if the query should be updated.
 * @param dataTransformer - A function that receives the current cached data and returns the new data to be set.
 * @returns The result of the dataTransformer for each updated query.
 */
export const setQueriesData = <PayloadT, DataT>(
  queryClient: QueryClient,
  {
    route,
    predicate,
    dataTransformer,
  }: {
    route: string;
    predicate: (payload: PayloadT) => boolean;
    dataTransformer: (oldData: DataT | undefined) => DataT | undefined;
  },
) => {
  queryClient.setQueriesData(
    {
      predicate: query => {
        const internalQueryKey = query.queryKey.find(isInternalQueryKey);
        if (!internalQueryKey) {
          return false;
        }
        if (internalQueryKey.route !== route) {
          return false;
        }
        const payload = internalQueryKey.payload as PayloadT;
        return predicate(payload);
      },
    },
    dataTransformer,
  );
};
